import { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";

const adminNavItems = [
  { path: "/admin/dashboard", icon: "ri-dashboard-line", label: "Dashboard" },
  { path: "/admin/users", icon: "ri-user-settings-line", label: "Kullanıcı Yönetimi" },
  { path: "/admin/dealers", icon: "ri-store-2-line", label: "Bayi Yönetimi" },
  { path: "/admin/dealer-admins", icon: "ri-shield-user-line", label: "Bayi Admin Tanımları" },
  { path: "/admin/dealer-currencies", icon: "ri-money-dollar-circle-line", label: "Bayi Para Birimi" },
  { path: "/admin/dealer-users", icon: "ri-user-star-line", label: "Bayi Kullanıcıları" },
  { path: "/admin/dealer-logs", icon: "ri-file-list-3-line", label: "Bayi Logları" },
  { path: "/admin/products", icon: "ri-smartphone-line", label: "Ürün Yönetimi" },
  { path: "/admin/categories", icon: "ri-folder-line", label: "Kategori Yönetimi" },
  { path: "/admin/data-import", icon: "ri-upload-cloud-2-line", label: "Veri Aktarımı" },
  { path: "/admin/bank-accounts", icon: "ri-bank-line", label: "Banka Tanımlamaları" },
  { path: "/admin/support", icon: "ri-customer-service-2-line", label: "Destek Talepleri" },
  { path: "/admin/settings", icon: "ri-settings-3-line", label: "Sistem Ayarları" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminUser, setAdminUser] = useState<{ username: string; role: string } | null>(null);
  const { theme, resolvedTheme, toggleTheme } = useTheme();

  useEffect(() => {
    const stored = sessionStorage.getItem("artpos_admin");
    if (!stored) {
      navigate("/admin/login");
      return;
    }
    setAdminUser(JSON.parse(stored));
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem("artpos_admin");
    navigate("/admin/login");
  };

  if (!adminUser) return null;

  return (
    <div className="min-h-screen flex bg-background-50">
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-black/30 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)}></div>
      )}

      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-background-50 border-r border-background-200/70 flex flex-col transition-transform duration-300 ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"} ${sidebarOpen ? "lg:w-64" : "lg:w-20"}`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-background-200/70 shrink-0">
          <div className={`flex items-center gap-3 ${!sidebarOpen && "lg:hidden"}`}>
            <img
              src="https://public.readdy.ai/ai/img_res/0bc3c21d-f175-4039-a061-cbb43a619b2e.png"
              alt="ART POS"
              className="w-11 h-11 object-contain"
            />
            <span className="font-bold text-foreground-950 text-sm whitespace-nowrap">ART POS</span>
          </div>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="hidden lg:flex w-8 h-8 items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 cursor-pointer">
            <i className={sidebarOpen ? "ri-contract-left-right-line" : "ri-expand-left-right-line"}></i>
          </button>
          <button onClick={() => setMobileMenuOpen(false)} className="lg:hidden w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-400 cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {adminNavItems.map((item) => (
            <NavLink key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap ${isActive ? "bg-accent-100 text-accent-700 font-medium" : "text-foreground-600 hover:bg-background-100 hover:text-foreground-900"} ${!sidebarOpen ? "lg:justify-center lg:px-0" : ""}`}>
              <i className={`${item.icon} text-lg shrink-0`}></i>
              <span className={!sidebarOpen ? "lg:hidden" : ""}>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-background-200/70 p-3">
          <div className={`flex items-center gap-3 px-3 py-2 ${!sidebarOpen && "lg:justify-center lg:px-0"}`}>
            <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
              <i className="ri-user-line text-accent-600 text-sm"></i>
            </div>
            <div className={`flex-1 min-w-0 ${!sidebarOpen && "lg:hidden"}`}>
              <p className="text-sm font-medium text-foreground-900 truncate">{adminUser.username}</p>
              <p className="text-xs text-foreground-400">{adminUser.role === "super_admin" ? "Süper Admin" : "Admin"}</p>
            </div>
          </div>
          <button onClick={handleLogout} className={`w-full mt-2 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors cursor-pointer whitespace-nowrap ${!sidebarOpen ? "lg:justify-center lg:px-0" : ""}`}>
            <i className="ri-logout-box-line text-lg shrink-0"></i>
            <span className={!sidebarOpen ? "lg:hidden" : ""}>Çıkış Yap</span>
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-background-50 border-b border-background-200/70 flex items-center justify-between px-4 md:px-6 shrink-0">
          <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 text-foreground-500 cursor-pointer">
            <i className="ri-menu-line text-xl"></i>
          </button>
          <div className="flex-1"></div>
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="w-9 h-9 rounded-lg flex items-center justify-center bg-background-100 text-foreground-400 hover:bg-background-200/70 transition-colors cursor-pointer"
              title={
                theme === "system"
                  ? `Otomatik (${resolvedTheme === "dark" ? "Gece" : "Gündüz"})`
                  : theme === "dark"
                    ? "Gece Modu"
                    : "Gündüz Modu"
              }
            >
              <i className={
                resolvedTheme === "dark"
                  ? "ri-moon-line text-lg"
                  : "ri-sun-line text-lg"
              }></i>
            </button>
            <span className="text-sm text-foreground-500">{new Date().toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}</span>
            <div className="w-px h-6 bg-background-200/70"></div>
            <span className="text-sm font-medium text-foreground-700">{adminUser.username}</span>
          </div>
        </header>
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}